import { useEffect, useState, useCallback } from 'react';
import { Activity, AlertTriangle, Monitor, Smartphone, Users, RefreshCw, X, LogOut, Ban, CheckCircle2 } from 'lucide-react';
import { api } from '../../../api/client';
import { ConfirmDialog } from '../../../components/ConfirmDialog';

type UserSession = {
  id: string;
  userId: string;
  username: string | null;
  fullName: string | null;
  deviceId: string;
  deviceName: string | null;
  locationName: string | null;
  loginAt: string;
  expiresAt: string;
  status: string;
};

type OperatorSession = {
  id: string;
  operatorId: string;
  operatorDisplayName: string | null;
  deviceId: string;
  deviceName: string | null;
  locationName: string | null;
  shiftId: string | null;
  startedAt: string;
  endedAt: string | null;
  status: string;
};

type Device = {
  id: string;
  deviceFingerprint: string;
  deviceName: string;
  locationId: string;
  locationName: string | null;
  status: string;
  /** Da li je neko trenutno prijavljen sa ovog uredjaja - zaseban podatak od `status`. */
  inUse?: boolean;
  registeredAt: string;
  lastSeenAt: string;
};

type View = 'users' | 'operators' | 'devices';

export function SessionsTab({ tenantId }: { tenantId: string }) {
  const [view, setView] = useState<View>('operators');
  const [userSessions, setUserSessions] = useState<UserSession[]>([]);
  const [operatorSessions, setOperatorSessions] = useState<OperatorSession[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<UserSession | null>(null);
  const [deviceTarget, setDeviceTarget] = useState<Device | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, o, d] = await Promise.all([
        api.get<UserSession[]>(`/admin/tenants/${tenantId}/sessions/users`),
        api.get<OperatorSession[]>(`/admin/tenants/${tenantId}/sessions/operators`),
        api.get<Device[]>(`/admin/tenants/${tenantId}/sessions/devices`),
      ]);
      setUserSessions(u);
      setOperatorSessions(o);
      setDevices(d);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { refresh(); }, [refresh]);

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    await api.del(`/admin/tenants/${tenantId}/sessions/users/${revokeTarget.id}`);
    refresh();
  };

  /**
   * Blokiranje uredjaja je jace od opoziva sesije: opoziv odjavljuje korisnika,
   * blokiranje mu oduzima kasu - sa blokiranog uredjaja se ne moze ni prijaviti.
   * Zato ima potvrdu, i zato ima povratak (odblokiranje).
   */
  const confirmDevice = async () => {
    if (!deviceTarget) return;
    const akcija = deviceTarget.status === 'active' ? 'block' : 'unblock';
    await api.post(`/admin/tenants/${tenantId}/sessions/devices/${deviceTarget.id}/${akcija}`, {});
    setDeviceTarget(null);
    refresh();
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const activeUserSessions = userSessions.filter((s) => s.status === 'active');
  const activeOperatorSessions = operatorSessions.filter((s) => s.status === 'active' && !s.endedAt);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--color-text-strong)', fontWeight: 600 }}>Aktivne sesije</h3>
        <div style={{ flex: 1 }} />
        <button onClick={refresh} disabled={loading} style={btnSecondary}>
          <RefreshCw size={13} /> Osveži
        </button>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

      {/* View tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
        <SubTab active={view === 'operators'} onClick={() => setView('operators')} icon={<Users size={13} />} label={`POS operateri (${activeOperatorSessions.length})`} />
        <SubTab active={view === 'users'} onClick={() => setView('users')} icon={<Monitor size={13} />} label={`Web login (${activeUserSessions.length})`} />
        <SubTab active={view === 'devices'} onClick={() => setView('devices')} icon={<Smartphone size={13} />} label={`Uređaji (${devices.length})`} />
      </div>

      {/* Operator sessions */}
      {view === 'operators' && (
        <div style={card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%' }}>
              <thead><tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Operater</th>
                <th style={th}>Lokacija</th>
                <th style={th}>Uređaj</th>
                <th style={th}>Početak shift-a</th>
                <th style={th}>Status</th>
              </tr></thead>
              <tbody>
                {operatorSessions.length === 0 && (
                  <tr><td colSpan={5} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
                    <Activity size={28} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                    Nema operator sesija
                  </td></tr>
                )}
                {operatorSessions.map((s) => {
                  const isActive = s.status === 'active' && !s.endedAt;
                  return (
                    <tr key={s.id}>
                      <td style={{ ...td, fontWeight: 600, color: 'var(--color-text-strong)' }}>{s.operatorDisplayName || '-'}</td>
                      <td style={{ ...td, color: 'var(--color-text-muted)' }}>{s.locationName || '-'}</td>
                      <td style={{ ...td, color: 'var(--color-text-muted)', fontFamily: 'monospace', fontSize: 11 }}>{s.deviceName || '-'}</td>
                      <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 11 }}>{formatDate(s.startedAt)}</td>
                      <td style={td}>
                        {isActive
                          ? <span style={badgeActive}>Aktivna</span>
                          : <span style={badgeEnded}>Završena {s.endedAt ? `· ${formatDate(s.endedAt)}` : ''}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User sessions */}
      {view === 'users' && (
        <div style={card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%' }}>
              <thead><tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Korisnik</th>
                <th style={th}>Uređaj</th>
                <th style={th}>Lokacija</th>
                <th style={th}>Login</th>
                <th style={th}>Ističe</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right', width: 80 }}>Akcije</th>
              </tr></thead>
              <tbody>
                {userSessions.length === 0 && (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
                    <Monitor size={28} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                    Nema web sesija
                  </td></tr>
                )}
                {userSessions.map((s) => (
                  <tr key={s.id}>
                    <td style={{ ...td, fontWeight: 600, color: 'var(--color-text-strong)' }}>
                      {s.fullName || '-'}
                      {s.username && <div style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>{s.username}</div>}
                    </td>
                    <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 11 }}>{s.deviceName || '-'}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)' }}>{s.locationName || '-'}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 11 }}>{formatDate(s.loginAt)}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 11 }}>{formatDate(s.expiresAt)}</td>
                    <td style={td}>
                      {s.status === 'active'
                        ? <span style={badgeActive}>Aktivna</span>
                        : <span style={badgeEnded}>{s.status}</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {s.status === 'active' && (
                        <button onClick={() => setRevokeTarget(s)} style={{ ...iconBtn, color: '#fca5a5' }} title="Revoke session">
                          <LogOut size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Devices */}
      {view === 'devices' && (
        <div style={card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%' }}>
              <thead><tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Naziv</th>
                <th style={th}>Lokacija</th>
                <th style={th}>Fingerprint</th>
                <th style={th}>Registrovan</th>
                <th style={th}>Posl. seen</th>
                <th style={th}>U upotrebi</th>
                <th style={th}>Status</th>
                <th style={th}></th>
              </tr></thead>
              <tbody>
                {devices.length === 0 && (
                  <tr><td colSpan={8} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
                    <Smartphone size={28} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                    Nema registrovanih uređaja
                  </td></tr>
                )}
                {devices.map((d) => (
                  <tr key={d.id}>
                    <td style={{ ...td, fontWeight: 600, color: 'var(--color-text-strong)' }}>{d.deviceName}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)' }}>{d.locationName || '-'}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)', fontFamily: 'monospace', fontSize: 11 }}>{d.deviceFingerprint.slice(0, 16)}...</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 11 }}>{formatDate(d.registeredAt)}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 11 }}>{formatDate(d.lastSeenAt)}</td>
                    {/*
                      Prisustvo je ODVOJENO od dozvole. `status` kaze da li uredjaj
                      SME da radi; „U upotrebi" da li je neko trenutno prijavljen.
                      Ranije je stajao samo `status`, pa je zapis ostajao „Aktivan"
                      i posle opoziva sesije - izgledalo je kao da kasa i dalje radi.
                    */}
                    <td style={td}>
                      {d.inUse
                        ? <span style={badgeActive}>U upotrebi</span>
                        : <span style={badgeEnded}>Slobodan</span>}
                    </td>
                    <td style={td}>
                      {d.status === 'active'
                        ? <span style={badgeActive}>Dozvoljen</span>
                        : <span style={badgeEnded}>Blokiran</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {d.status === 'active' ? (
                        <button onClick={() => setDeviceTarget(d)} style={{ ...iconBtn, color: '#fca5a5' }} title="Blokiraj uređaj">
                          <Ban size={13} />
                        </button>
                      ) : (
                        <button onClick={() => setDeviceTarget(d)} style={{ ...iconBtn, color: '#86efac' }} title="Odblokiraj uređaj">
                          <CheckCircle2 size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {deviceTarget && (
        <ConfirmDialog
          title={deviceTarget.status === 'active' ? 'Blokiraj uređaj' : 'Odblokiraj uređaj'}
          message={deviceTarget.status === 'active'
            ? `Blokirati "${deviceTarget.deviceName}"? Sve sesije na tom uređaju se odmah gase, a nova prijava sa njega neće biti moguća dok ga ne odblokirate.`
            : `Odblokirati "${deviceTarget.deviceName}"? Uređaj će ponovo moći da se prijavi. Ranije sesije se ne vraćaju - korisnik se prijavljuje iznova.`}
          confirmLabel={deviceTarget.status === 'active' ? 'Blokiraj' : 'Odblokiraj'}
          variant={deviceTarget.status === 'active' ? 'danger' : 'default'}
          onConfirm={confirmDevice}
          onClose={() => setDeviceTarget(null)}
        />
      )}

      {revokeTarget && (
        <ConfirmDialog
          title="Revoke web sesiju"
          message={`Da li želite da prekinete web sesiju za "${revokeTarget.fullName || revokeTarget.username}"? Korisnik će biti odjavljen i moraće ponovo da se loguje.`}
          confirmLabel="Revoke"
          variant="danger"
          onConfirm={confirmRevoke}
          onClose={() => setRevokeTarget(null)}
        />
      )}
    </div>
  );
}

function SubTab({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '8px 14px', border: 'none', cursor: 'pointer',
        flexShrink: 0, whiteSpace: 'nowrap',
        background: 'transparent',
        color: active ? 'var(--color-text-strong)' : 'var(--color-text-muted)',
        fontSize: 12, fontWeight: active ? 600 : 500,
        borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
        marginBottom: -1,
      }}
    >
      {icon} {label}
    </button>
  );
}

const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 12, fontWeight: 500, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const badgeActive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', fontWeight: 700 };
const badgeEnded: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(100,116,139,0.2)', color: '#94a3b8', fontWeight: 700 };
